import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency } from './helper';


const COLORS = {
    primary: [37, 99, 235],
    success: [16, 185, 129],
    warning: [245, 158, 11],
    danger: [239, 68, 68],
    dark: [30, 41, 59],
    gray: [100, 116, 139],
    light: [241, 245, 249],
};


const STATUS_CONFIG = {
    ok: { label: 'Compliant', color: COLORS.success },
    warning: { label: 'At Risk', color: COLORS.warning },
    critical: { label: 'Breach', color: COLORS.danger },
};


export const generateLoanReport = (loan, alerts = [], breachHistory = []) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    let yPos = margin;


    const addText = (text, x, y, options = {}) => {
        const { fontSize = 10, fontStyle = 'normal', color = COLORS.dark } = options;
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        doc.setTextColor(...color);
        doc.text(text, x, y);
        return y + (fontSize * 0.5);
    };

    const addLine = (y, color = COLORS.light) => {
        doc.setDrawColor(...color);
        doc.setLineWidth(0.5);
        doc.line(margin, y, pageWidth - margin, y);
        return y + 5;
    };

    const checkPageBreak = (neededSpace) => {
        if (yPos + neededSpace > pageHeight - margin) {
            doc.addPage();
            yPos = margin;
            return true;
        }
        return false;
    };






    doc.setFillColor(...COLORS.primary);
    doc.rect(0, 0, pageWidth, 40, 'F');

    addText('COVENANT TRACKER', margin, 18, {
        fontSize: 20,
        fontStyle: 'bold',
        color: [255, 255, 255]
    });
    addText('Loan Compliance Report', margin, 28, {
        fontSize: 12,
        color: [200, 220, 255]
    });


    const reportDate = new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
    addText(`Generated: ${reportDate}`, pageWidth - margin - 50, 28, {
        fontSize: 9,
        color: [200, 220, 255]
    });

    yPos = 55;






    addText('LOAN SUMMARY', margin, yPos, {
        fontSize: 14,
        fontStyle: 'bold',
        color: COLORS.primary
    });


    const statusConfig = STATUS_CONFIG[loan.status];
    const badgeWidth = 28;
    const badgeX = pageWidth - margin - badgeWidth;
    doc.setFillColor(...statusConfig.color);
    doc.roundedRect(badgeX, yPos - 5, badgeWidth, 8, 2, 2, 'F');


    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    const statusLabelWidth = doc.getTextWidth(statusConfig.label);
    doc.text(statusConfig.label, badgeX + (badgeWidth - statusLabelWidth) / 2, yPos);

    yPos += 12;


    const loanInfo = [
        ['Borrower', loan.borrower],
        ['Amount', formatCurrency(loan.amount)],
        ['Lead Arranger', loan.leadArranger],
        ['Sector', loan.sector],
        ['Country', loan.country],
        ['Start Date', new Date(loan.startDate).toLocaleDateString('en-GB')],
        ['Maturity Date', new Date(loan.maturityDate).toLocaleDateString('en-GB')],
        ['Next Reporting', new Date(loan.nextReportingDate).toLocaleDateString('en-GB')],
    ];

    const labelOffset = 35;
    const colWidth = (pageWidth - margin * 2) / 2;

    loanInfo.forEach((item, index) => {
        const col = index % 2;
        const row = Math.floor(index / 2);
        const x = margin + (col * colWidth);
        const y = yPos + (row * 10);


        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.gray);
        doc.text(item[0] + ':', x, y);


        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.dark);
        doc.text(item[1], x + labelOffset, y);
    });

    yPos += Math.ceil(loanInfo.length / 2) * 10 + 8;
    yPos = addLine(yPos);





    checkPageBreak(60);

    addText('COVENANT STATUS', margin, yPos, {
        fontSize: 14,
        fontStyle: 'bold',
        color: COLORS.primary
    });
    yPos += 8;


    const okCount = loan.covenants.filter(c => c.status === 'ok').length;
    const warningCount = loan.covenants.filter(c => c.status === 'warning').length;
    const criticalCount = loan.covenants.filter(c => c.status === 'critical').length;

    const statsText = `${loan.covenants.length} Total | ${okCount} Compliant | ${warningCount} At Risk | ${criticalCount} Breach`;
    addText(statsText, margin, yPos, { fontSize: 9, color: COLORS.gray });
    yPos += 8;


    const covenantTableData = loan.covenants.map(covenant => {
        const headroom = covenant.operator === '<='
            ? ((covenant.threshold - covenant.current) / covenant.threshold * 100).toFixed(1)
            : ((covenant.current - covenant.threshold) / covenant.threshold * 100).toFixed(1);

        return [
            covenant.name,
            `${covenant.operator} ${covenant.threshold}`,
            covenant.current.toString(),
            `${headroom}%`,
            STATUS_CONFIG[covenant.status].label,
        ];
    });

    autoTable(doc, {
        startY: yPos,
        head: [['Covenant', 'Threshold', 'Current', 'Headroom', 'Status']],
        body: covenantTableData,
        margin: { left: margin, right: margin },
        styles: {
            fontSize: 9,
            cellPadding: 4,
        },
        headStyles: {
            fillColor: COLORS.dark,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
        },
        columnStyles: {
            0: { cellWidth: 50 },
            4: { cellWidth: 25, halign: 'center' },
        },
        didParseCell: (data) => {

            if (data.section === 'body' && data.column.index === 4) {
                const status = loan.covenants[data.row.index]?.status;
                if (status) {
                    data.cell.styles.textColor = STATUS_CONFIG[status].color;
                    data.cell.styles.fontStyle = 'bold';
                }
            }

            if (data.section === 'body' && data.column.index === 3) {
                const value = parseFloat(data.cell.text);
                if (value < 0) {
                    data.cell.styles.textColor = COLORS.danger;
                } else if (value < 10) {
                    data.cell.styles.textColor = COLORS.warning;
                } else {
                    data.cell.styles.textColor = COLORS.success;
                }
            }
        },
    });

    yPos = doc.lastAutoTable.finalY + 15;





    if (alerts.length > 0) {
        checkPageBreak(50);

        addText('ACTIVE ALERTS', margin, yPos, {
            fontSize: 14,
            fontStyle: 'bold',
            color: COLORS.primary
        });
        yPos += 8;

        const alertTableData = alerts.map(alert => [
            alert.type.toUpperCase(),
            alert.title,
            alert.message,
            new Date(alert.createdAt).toLocaleDateString('en-GB'),
        ]);

        autoTable(doc, {
            startY: yPos,
            head: [['Severity', 'Title', 'Description', 'Date']],
            body: alertTableData,
            margin: { left: margin, right: margin },
            styles: {
                fontSize: 8,
                cellPadding: 3,
            },
            headStyles: {
                fillColor: COLORS.dark,
                textColor: [255, 255, 255],
                fontStyle: 'bold',
            },
            columnStyles: {
                0: { cellWidth: 20, halign: 'center' },
                2: { cellWidth: 70 },
                3: { cellWidth: 25 },
            },
            didParseCell: (data) => {
                if (data.section === 'body' && data.column.index === 0) {
                    const type = data.cell.text[0]?.toLowerCase();
                    if (type === 'critical') {
                        data.cell.styles.textColor = COLORS.danger;
                        data.cell.styles.fontStyle = 'bold';
                    } else if (type === 'warning') {
                        data.cell.styles.textColor = COLORS.warning;
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            },
        });

        yPos = doc.lastAutoTable.finalY + 15;
    }





    if (breachHistory.length > 0) {
        checkPageBreak(50);

        addText('BREACH HISTORY', margin, yPos, {
            fontSize: 14,
            fontStyle: 'bold',
            color: COLORS.primary
        });
        yPos += 8;

        const breachTableData = breachHistory.map(breach => [
            new Date(breach.date).toLocaleDateString('en-GB'),
            breach.covenant,
            breach.value.toString(),
            breach.threshold.toString(),
            breach.status === 'resolved' ? 'Resolved' : 'Active',
        ]);

        autoTable(doc, {
            startY: yPos,
            head: [['Date', 'Covenant', 'Value', 'Threshold', 'Status']],
            body: breachTableData,
            margin: { left: margin, right: margin },
            styles: {
                fontSize: 8,
                cellPadding: 3,
            },
            headStyles: {
                fillColor: COLORS.dark,
                textColor: [255, 255, 255],
                fontStyle: 'bold',
            },
            didParseCell: (data) => {
                if (data.section === 'body' && data.column.index === 4) {
                    const status = data.cell.text[0];
                    if (status === 'Resolved') {
                        data.cell.styles.textColor = COLORS.success;
                    } else {
                        data.cell.styles.textColor = COLORS.danger;
                    }
                    data.cell.styles.fontStyle = 'bold';
                }
            },
        });

        yPos = doc.lastAutoTable.finalY + 15;
    }





    const addFooter = () => {
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);


            doc.setDrawColor(...COLORS.light);
            doc.setLineWidth(0.5);
            doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);


            doc.setFontSize(8);
            doc.setTextColor(...COLORS.gray);
            doc.text(
                'Covenant Tracker - LMA Edge Hackathon 2025',
                margin,
                pageHeight - 8
            );
            doc.text(
                `Page ${i} of ${pageCount}`,
                pageWidth - margin - 20,
                pageHeight - 8
            );
        }
    };

    addFooter();

    return doc;
};


export const downloadLoanReport = (loan, alerts = [], breachHistory = []) => {
    const doc = generateLoanReport(loan, alerts, breachHistory);
    const fileName = `Covenant_Report_${loan.borrower.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
};


export const getLoanReportBlob = (loan, alerts = [], breachHistory = []) => {
    const doc = generateLoanReport(loan, alerts, breachHistory);
    return doc.output('blob');
};


export const getLoanReportBase64 = (loan, alerts = [], breachHistory = []) => {
    const doc = generateLoanReport(loan, alerts, breachHistory);
    return doc.output('datauristring').split(',')[1];
};


export const generatePortfolioReport = (loans, stats) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;
    let yPos = margin;


    doc.setFillColor(...COLORS.primary);
    doc.rect(0, 0, pageWidth, 40, 'F');

    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('COVENANT TRACKER', margin, 18);

    doc.setFontSize(12);
    doc.setTextColor(200, 220, 255);
    doc.text('Portfolio Summary Report', margin, 28);

    yPos = 55;


    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text('PORTFOLIO OVERVIEW', margin, yPos);
    yPos += 15;

    const kpis = [
        ['Total Loans', stats.totalLoans.toString()],
        ['Portfolio Value', formatCurrency(stats.totalValue)],
        ['Compliance Rate', `${stats.complianceRate}%`],
        ['At Risk', `${stats.atRisk} (${stats.critical} critical, ${stats.warning} warning)`],
    ];

    const kpiWidth = (pageWidth - margin * 2) / 2;
    kpis.forEach((kpi, index) => {
        const col = index % 2;
        const row = Math.floor(index / 2);
        const x = margin + (col * kpiWidth);
        const y = yPos + (row * 20);

        doc.setFontSize(9);
        doc.setTextColor(...COLORS.gray);
        doc.text(kpi[0], x, y);

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.dark);
        doc.text(kpi[1], x, y + 8);
    });

    yPos += 50;


    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text('LOANS REQUIRING ATTENTION', margin, yPos);
    yPos += 8;

    const atRiskLoans = loans.filter(l => l.status !== 'ok');
    const tableData = atRiskLoans.map(loan => [
        loan.borrower,
        formatCurrency(loan.amount),
        loan.sector,
        `${loan.covenants.filter(c => c.status !== 'ok').length} / ${loan.covenants.length}`,
        STATUS_CONFIG[loan.status].label,
    ]);

    autoTable(doc, {
        startY: yPos,
        head: [['Borrower', 'Amount', 'Sector', 'Issues', 'Status']],
        body: tableData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: {
            fillColor: COLORS.dark,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
        },
        didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 4) {
                const loanItem = atRiskLoans[data.row.index];
                if (loanItem) {
                    data.cell.styles.textColor = STATUS_CONFIG[loanItem.status].color;
                    data.cell.styles.fontStyle = 'bold';
                }
            }
        },
    });

    return doc;
};

export const downloadPortfolioReport = (loans, stats) => {
    const doc = generatePortfolioReport(loans, stats);
    const fileName = `Portfolio_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
};


export const generateFullLoanReport = (loan, alerts = [], breachHistory = [], historicalData = []) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    let yPos = margin;


    const addText = (text, x, y, options = {}) => {
        const { fontSize = 10, fontStyle = 'normal', color = COLORS.dark } = options;
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        doc.setTextColor(...color);
        doc.text(text, x, y);
        return y + (fontSize * 0.5);
    };

    const addLine = (y, color = COLORS.light) => {
        doc.setDrawColor(...color);
        doc.setLineWidth(0.5);
        doc.line(margin, y, pageWidth - margin, y);
        return y + 5;
    };

    const checkPageBreak = (neededSpace) => {
        if (yPos + neededSpace > pageHeight - margin) {
            doc.addPage();
            yPos = margin;
            return true;
        }
        return false;
    };




    doc.setFillColor(...COLORS.primary);
    doc.rect(0, 0, pageWidth, 40, 'F');

    addText('COVENANT TRACKER', margin, 18, {
        fontSize: 20,
        fontStyle: 'bold',
        color: [255, 255, 255]
    });
    addText('Full Compliance Report with Historical Data', margin, 28, {
        fontSize: 12,
        color: [200, 220, 255]
    });

    const reportDate = new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
    addText(`Generated: ${reportDate}`, pageWidth - margin - 50, 28, {
        fontSize: 9,
        color: [200, 220, 255]
    });

    yPos = 55;




    addText('EXECUTIVE SUMMARY', margin, yPos, {
        fontSize: 14,
        fontStyle: 'bold',
        color: COLORS.primary
    });
    yPos += 10;


    const okCount = loan.covenants.filter(c => c.status === 'ok').length;
    const warningCount = loan.covenants.filter(c => c.status === 'warning').length;
    const criticalCount = loan.covenants.filter(c => c.status === 'critical').length;
    const complianceRate = ((okCount / loan.covenants.length) * 100).toFixed(0);


    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, yPos, pageWidth - margin * 2, 35, 3, 3, 'F');

    const summaryItems = [
        { label: 'Compliance Rate', value: `${complianceRate}%`, color: parseInt(complianceRate) >= 80 ? COLORS.success : COLORS.danger },
        { label: 'Total Covenants', value: loan.covenants.length.toString(), color: COLORS.dark },
        { label: 'Breaches', value: criticalCount.toString(), color: criticalCount > 0 ? COLORS.danger : COLORS.success },
        { label: 'Warnings', value: warningCount.toString(), color: warningCount > 0 ? COLORS.warning : COLORS.success },
    ];

    const boxWidth = (pageWidth - margin * 2) / 4;
    summaryItems.forEach((item, index) => {
        const x = margin + (index * boxWidth) + 10;
        addText(item.label, x, yPos + 12, { fontSize: 8, color: COLORS.gray });
        addText(item.value, x, yPos + 24, { fontSize: 16, fontStyle: 'bold', color: item.color });
    });

    yPos += 45;





    addText('LOAN DETAILS', margin, yPos, {
        fontSize: 14,
        fontStyle: 'bold',
        color: COLORS.primary
    });


    const statusConfig = STATUS_CONFIG[loan.status];
    const badgeWidth = 28;
    const badgeX = pageWidth - margin - badgeWidth;
    doc.setFillColor(...statusConfig.color);
    doc.roundedRect(badgeX, yPos - 5, badgeWidth, 8, 2, 2, 'F');


    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    const labelWidth = doc.getTextWidth(statusConfig.label);
    doc.text(statusConfig.label, badgeX + (badgeWidth - labelWidth) / 2, yPos);

    yPos += 12;


    const loanInfo = [
        ['Borrower', loan.borrower],
        ['Amount', formatCurrency(loan.amount)],
        ['Lead Arranger', loan.leadArranger],
        ['Sector', loan.sector],
        ['Country', loan.country],
        ['Start Date', new Date(loan.startDate).toLocaleDateString('en-GB')],
        ['Maturity Date', new Date(loan.maturityDate).toLocaleDateString('en-GB')],
        ['Next Reporting', new Date(loan.nextReportingDate).toLocaleDateString('en-GB')],
    ];

    const labelWidth2 = 35;
    const colWidth = (pageWidth - margin * 2) / 2;

    loanInfo.forEach((item, index) => {
        const col = index % 2;
        const row = Math.floor(index / 2);
        const x = margin + (col * colWidth);
        const y = yPos + (row * 10);


        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.gray);
        doc.text(item[0] + ':', x, y);


        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.dark);
        doc.text(item[1], x + labelWidth2, y);
    });

    yPos += Math.ceil(loanInfo.length / 2) * 10 + 8;
    yPos = addLine(yPos);




    checkPageBreak(60);

    addText('COVENANT STATUS', margin, yPos, {
        fontSize: 14,
        fontStyle: 'bold',
        color: COLORS.primary
    });
    yPos += 8;

    const statsText = `${loan.covenants.length} Total | ${okCount} Compliant | ${warningCount} At Risk | ${criticalCount} Breach`;
    addText(statsText, margin, yPos, { fontSize: 9, color: COLORS.gray });
    yPos += 8;

    const covenantTableData = loan.covenants.map(covenant => {
        const headroom = covenant.operator === '<='
            ? ((covenant.threshold - covenant.current) / covenant.threshold * 100).toFixed(1)
            : ((covenant.current - covenant.threshold) / covenant.threshold * 100).toFixed(1);

        return [
            covenant.name,
            `${covenant.operator} ${covenant.threshold}`,
            covenant.current.toString(),
            `${headroom}%`,
            STATUS_CONFIG[covenant.status].label,
        ];
    });

    autoTable(doc, {
        startY: yPos,
        head: [['Covenant', 'Threshold', 'Current', 'Headroom', 'Status']],
        body: covenantTableData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: {
            fillColor: COLORS.dark,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
        },
        columnStyles: {
            0: { cellWidth: 50 },
            4: { cellWidth: 25, halign: 'center' },
        },
        didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 4) {
                const status = loan.covenants[data.row.index]?.status;
                if (status) {
                    data.cell.styles.textColor = STATUS_CONFIG[status].color;
                    data.cell.styles.fontStyle = 'bold';
                }
            }
            if (data.section === 'body' && data.column.index === 3) {
                const value = parseFloat(data.cell.text);
                if (value < 0) {
                    data.cell.styles.textColor = COLORS.danger;
                } else if (value < 10) {
                    data.cell.styles.textColor = COLORS.warning;
                } else {
                    data.cell.styles.textColor = COLORS.success;
                }
            }
        },
    });

    yPos = doc.lastAutoTable.finalY + 15;




    if (historicalData.length > 0) {
        checkPageBreak(80);

        addText('HISTORICAL TRENDS (6 MONTHS)', margin, yPos, {
            fontSize: 14,
            fontStyle: 'bold',
            color: COLORS.primary
        });
        yPos += 8;

        addText('Covenant values over the past 6 months showing compliance trajectory', margin, yPos, {
            fontSize: 9,
            color: COLORS.gray
        });
        yPos += 10;


        const months = historicalData.map(d => d.month);
        const covenantNames = loan.covenants.map(c => c.name);

        const histTableHead = ['Covenant', ...months.slice(-6)];
        const histTableBody = covenantNames.map(name => {
            const values = historicalData.slice(-6).map(d => {
                const val = d[name];
                return val !== undefined ? val.toFixed(2) : '-';
            });
            return [name, ...values];
        });

        autoTable(doc, {
            startY: yPos,
            head: [histTableHead],
            body: histTableBody,
            margin: { left: margin, right: margin },
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: {
                fillColor: COLORS.dark,
                textColor: [255, 255, 255],
                fontStyle: 'bold',
            },
            columnStyles: {
                0: { cellWidth: 40, fontStyle: 'bold' },
            },
        });

        yPos = doc.lastAutoTable.finalY + 15;
    }




    if (alerts.length > 0) {
        checkPageBreak(50);

        addText('ACTIVE ALERTS', margin, yPos, {
            fontSize: 14,
            fontStyle: 'bold',
            color: COLORS.primary
        });
        yPos += 8;

        const alertTableData = alerts.map(alert => [
            alert.type.toUpperCase(),
            alert.title,
            alert.message,
            new Date(alert.createdAt).toLocaleDateString('en-GB'),
        ]);

        autoTable(doc, {
            startY: yPos,
            head: [['Severity', 'Title', 'Description', 'Date']],
            body: alertTableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: {
                fillColor: COLORS.dark,
                textColor: [255, 255, 255],
                fontStyle: 'bold',
            },
            columnStyles: {
                0: { cellWidth: 20, halign: 'center' },
                2: { cellWidth: 70 },
                3: { cellWidth: 25 },
            },
            didParseCell: (data) => {
                if (data.section === 'body' && data.column.index === 0) {
                    const type = data.cell.text[0]?.toLowerCase();
                    if (type === 'critical') {
                        data.cell.styles.textColor = COLORS.danger;
                        data.cell.styles.fontStyle = 'bold';
                    } else if (type === 'warning') {
                        data.cell.styles.textColor = COLORS.warning;
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            },
        });

        yPos = doc.lastAutoTable.finalY + 15;
    }




    if (breachHistory.length > 0) {
        checkPageBreak(50);

        addText('BREACH HISTORY', margin, yPos, {
            fontSize: 14,
            fontStyle: 'bold',
            color: COLORS.primary
        });
        yPos += 8;

        addText(`${breachHistory.filter(b => b.status === 'resolved').length} resolved, ${breachHistory.filter(b => b.status !== 'resolved').length} active`, margin, yPos, {
            fontSize: 9,
            color: COLORS.gray
        });
        yPos += 8;

        const breachTableData = breachHistory.map(breach => [
            new Date(breach.date).toLocaleDateString('en-GB'),
            breach.covenant,
            breach.value.toString(),
            breach.threshold.toString(),
            breach.status === 'resolved' ? 'Resolved' : 'Active',
        ]);

        autoTable(doc, {
            startY: yPos,
            head: [['Date', 'Covenant', 'Value', 'Threshold', 'Status']],
            body: breachTableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: {
                fillColor: COLORS.dark,
                textColor: [255, 255, 255],
                fontStyle: 'bold',
            },
            didParseCell: (data) => {
                if (data.section === 'body' && data.column.index === 4) {
                    const status = data.cell.text[0];
                    if (status === 'Resolved') {
                        data.cell.styles.textColor = COLORS.success;
                    } else {
                        data.cell.styles.textColor = COLORS.danger;
                    }
                    data.cell.styles.fontStyle = 'bold';
                }
                if (data.section === 'body' && data.column.index === 2) {
                    data.cell.styles.textColor = COLORS.danger;
                    data.cell.styles.fontStyle = 'bold';
                }
            },
        });

        yPos = doc.lastAutoTable.finalY + 15;
    }




    checkPageBreak(60);

    addText('RISK ASSESSMENT', margin, yPos, {
        fontSize: 14,
        fontStyle: 'bold',
        color: COLORS.primary
    });
    yPos += 10;


    const riskFactors = [];
    if (criticalCount > 0) {
        riskFactors.push({ level: 'HIGH', text: `${criticalCount} covenant(s) currently in breach requiring immediate attention` });
    }
    if (warningCount > 0) {
        riskFactors.push({ level: 'MEDIUM', text: `${warningCount} covenant(s) at risk of breach within tolerance thresholds` });
    }
    if (breachHistory.filter(b => b.status !== 'resolved').length > 0) {
        riskFactors.push({ level: 'HIGH', text: 'Unresolved historical breaches on record' });
    }
    if (alerts.filter(a => a.type === 'critical').length > 0) {
        riskFactors.push({ level: 'HIGH', text: `${alerts.filter(a => a.type === 'critical').length} critical alert(s) pending review` });
    }
    if (riskFactors.length === 0) {
        riskFactors.push({ level: 'LOW', text: 'All covenants compliant with no outstanding issues' });
    }

    riskFactors.forEach(risk => {
        const riskColor = risk.level === 'HIGH' ? COLORS.danger : risk.level === 'MEDIUM' ? COLORS.warning : COLORS.success;
        doc.setFillColor(...riskColor);
        doc.circle(margin + 3, yPos - 2, 2, 'F');
        addText(`[${risk.level}]`, margin + 8, yPos, { fontSize: 8, fontStyle: 'bold', color: riskColor });
        addText(risk.text, margin + 30, yPos, { fontSize: 8, color: COLORS.dark });
        yPos += 10;
    });

    yPos += 5;




    const addFooter = () => {
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);

            doc.setDrawColor(...COLORS.light);
            doc.setLineWidth(0.5);
            doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

            doc.setFontSize(8);
            doc.setTextColor(...COLORS.gray);
            doc.text(
                'Covenant Tracker - Full Compliance Report - LMA Edge Hackathon 2025',
                margin,
                pageHeight - 8
            );
            doc.text(
                `Page ${i} of ${pageCount}`,
                pageWidth - margin - 20,
                pageHeight - 8
            );
        }
    };

    addFooter();

    return doc;
};


export const downloadFullLoanReport = (loan, alerts = [], breachHistory = [], historicalData = []) => {
    const doc = generateFullLoanReport(loan, alerts, breachHistory, historicalData);
    const fileName = `Full_Covenant_Report_${loan.borrower.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
};